// The font sheet's choices as data (unify-ui ship 4a): which rows the curated view and the catalog view show, in which
// order, and what a catalog row IS. Pure — no DOM — so packages/ui/test/picker.test.js holds it against the real lists;
// picker.js and catalog-view.js draw what it returns. Nothing of the core is imported here (ruling R87): the tool hands
// in the functions (`core`: fontRank, defaultWeight, curatedEntry, CJK_TAGS, MOODS — see font-control.js).

// The mood tabs: 'all', then each mood some family of the list has (an empty mood is hidden).
export const moodTabs = (curated, MOODS) => ['all', ...MOODS.filter((m) => curated.some((f) => f.mood === m))];

// The curated view's rows: the families of the mood; while the language chip is on, only those that can set the text;
// a family that sets it fully first, one that sets Han-only text without a CJK script after (fontRank), a family that
// cannot set it last (chip off). Stable: the list's own order within a rank.
export function curatedRows({ curated, mood, cls, filterOn, core }) {
  return curated.filter((f) => mood === 'all' || f.mood === mood)
    .map((f) => ({ f, rank: core.fontRank(f.scripts, cls) }))
    .filter((x) => !filterOn || x.rank !== null)
    .sort((a, b) => (a.rank ?? 2) - (b.rank ?? 2))
    .map((x) => x.f);
}

export const CATEGORIES = ['all', 'sans-serif', 'serif', 'display', 'handwriting', 'monospace'];
// Icon fonts are not text: Material Icons and Material Symbols (9 families of ligature glyphs, listed under monospace) are
// left out of the sheet (ruling R111; the spec's "files with no extension" is stale: every catalog url ends in .ttf or .otf)
// (spec rev 2, minors).
export const ICON_FAMILY = /^Material (Icons|Symbols)\b/;
// The catalog view's rows: by category and a search of the family's name; the chip as in the curated view; the
// catalog's popularity order within a rank.
export function catalogMatches({ list, cat, q, cls, filterOn, core }) {
  const needle = String(q || '').trim().toLowerCase();
  return list.filter((f) => !ICON_FAMILY.test(f.family) && (cat === 'all' || f.category === cat) && (!needle || f.family.toLowerCase().includes(needle)))
    .map((f) => ({ f, rank: core.fontRank(f.subsets, cls) }))
    .filter((x) => !filterOn || x.rank !== null)
    .sort((a, b) => (a.rank ?? 2) - (b.rank ?? 2))
    .map((x) => x.f);
}

// What a catalog row is, at the family's default weight (ruling R95): the curated entry when one matches by family AND
// weight — a bundled one too — so the row has no "not yet print-tested" mark and a tap picks that entry; else a catalog
// font, marked, its size on the row. → { weight, match (the entry or null), pick (what onPick is handed), bytes, cjk }
export function catalogRow(f, { curated, core }) {
  const weight = core.defaultWeight(f.variants), v = f.variants[weight];
  const match = core.curatedEntry({ src: 'google', id: f.family, variant: weight }, curated) || null;
  return { weight, match, bytes: v.bytes, cjk: f.subsets.find((s) => core.CJK_TAGS.includes(s)) || null,
    pick: match || { src: 'google', id: f.family, family: f.family, variant: weight, bytes: v.bytes } };
}

// A design's font is "not yet print-tested" when it is a Google family that is not an entry of the list (R91, R95; I4:
// the line under the font button says so). A bundled font, a curated one, an uploaded one: no line.
export const untested = (font, curated, core) => !!font && font.src === 'google' && !core.curatedEntry(font, curated);
// The family a record shows under: the list's name for an entry, else the record's id (a Google id IS its family).
export const familyOf = (font, curated, core) => (font ? ((core.curatedEntry(font, curated) || {}).family || (font.src === 'upload' ? font.name || font.id : font.id)) : '');

// The weight select (spec "Ship 4a": it shows when the family has more than one weight). → { weights, value } — weights
// [] hides it. A Google family's weights are the catalog's; a bundled family's too, but only once the catalog is in the
// page already — it is never fetched for a bundled font (952 KB on every page that opens on one; the plan's question 2).
export function weightChoice(font, fam, curated, core) {
  if (!font || !fam || font.src === 'upload') return { weights: [], value: '' };
  const e = font.src === 'bundled' ? core.curatedEntry(font, curated) : null;
  const weights = Object.keys(fam.variants);
  const value = e ? e.variant : font.variant || core.defaultWeight(fam.variants);
  return { weights: weights.length > 1 ? weights : [], value };
}
